import type { CatalogUerBaseCostEstimateBasisRow, CollectionUerBaseCostEstimateBasisRow } from 'gql/graphql';
import type { GroupCollapseState } from 'pages/Project/UerProject/ActKC3List/components/ActKC3ListTable/types';

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ICommonUsedProperties } from './useUERBaseCostEstimateBasisRowsFiltered';

interface IGroupedItem {
    id: string;
    groupKey?: string;
    isGroup?: boolean;
    isGroupMember?: boolean;
}

export type TUerBaseCostEstimateBasisRow<T> = T & IGroupedItem;

interface IUERBaseCostEstimateBasisRowsGroupedData {
    items: Map<
        string,
        | TUerBaseCostEstimateBasisRow<CatalogUerBaseCostEstimateBasisRow>[]
        | TUerBaseCostEstimateBasisRow<CollectionUerBaseCostEstimateBasisRow>[]
    >;
    initialCollapseState: Record<string, boolean>;
}

interface IUseUERBaseCostEstimateBasisRowsGrouped {
    items?: ICommonUsedProperties[];
}

export const useUERBaseCostEstimateBasisRowsGrouped = (props: IUseUERBaseCostEstimateBasisRowsGrouped) => {
    const { items } = props;

    const groupedItems = useMemo(() => {
        const accInit: IUERBaseCostEstimateBasisRowsGroupedData = {
            items: new Map(),
            initialCollapseState: {},
        };

        if (!items?.length) {
            return {
                itemsWithGroups: [],
                initialCollapseState: {},
            };
        }

        const rootItems = items?.filter((i) => i.isSectionHeadLine || i.isPriceHeadLine);

        const groupedData = rootItems.reduce((acc, item) => {
            const id = item.rowId;
            const children = !item.isSectionHeadLine ? items?.filter((i) => i.parentRowId === id) : [];

            const topLevelItem = {
                ...item,
                id,
                isGroup: !!children?.length,
                groupKey: id,
                name: item.isSectionHeadLine ? `Раздел ${item.number}. ${item.name}` : item.name,
            };

            const subLevelItems: any[] =
                children?.map((c) => ({
                    ...c,
                    id: `${id}.${c.rowId}`,
                    groupKey: id,
                    isGroup: false,
                    isGroupMember: true,
                })) || [];

            acc.items.set(id, [topLevelItem, ...subLevelItems]);
            acc.initialCollapseState[id] = false;

            return acc;
        }, accInit);

        const itemsWithGroups = Array.from(groupedData.items.values()).flat();

        return {
            itemsWithGroups,
            initialCollapseState: groupedData.initialCollapseState,
        };
    }, [items]);

    const [collapseState, setCollapseState] = useState<GroupCollapseState>({});

    const actualGroupedItems = useMemo(
        () =>
            groupedItems?.itemsWithGroups.filter(
                (item: IGroupedItem) => item.groupKey && (item.isGroup || !collapseState[item.groupKey]),
            ),
        [collapseState, groupedItems?.itemsWithGroups],
    );

    useEffect(() => {
        if (groupedItems?.initialCollapseState) {
            setCollapseState((prevState) => ({ ...groupedItems?.initialCollapseState, ...prevState }));
        }
    }, [groupedItems?.initialCollapseState]);

    const changeCollapseState = useCallback(
        (groupKey: string) => () => {
            setCollapseState((prevState) => ({ ...prevState, [groupKey]: !prevState[groupKey] }));
        },
        [],
    );

    return {
        data: actualGroupedItems,
        changeCollapseState,
        collapseState,
    };
};
